import { useState, useEffect } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { ContractStatus } from "@/types";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

interface StatusSelectorProps {
  currentStatus: string;
  onChange: (newStatus: string) => void;
  disabled?: boolean;
}

export function StatusSelector({ currentStatus, onChange, disabled = false }: StatusSelectorProps) {
  const [status, setStatus] = useState<string>(currentStatus);
  const { user } = useAuth();
  const [isDisabled, setIsDisabled] = useState<boolean>(disabled);

  // Check user role permissions
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const isSuperAdmin = user?.role === 'super_admin';
  const isManager = user?.role === 'manager';

  // Check if the status is one that should display text instead of select
  const isStatusReadOnly = currentStatus === ContractStatus.Active ||
                            currentStatus === ContractStatus.Complete ||
                            currentStatus === "archived";

  useEffect(() => {
    // Update local status when prop changes
    setStatus(currentStatus);

    // Determine if the selector should be disabled
    if (disabled || isStatusReadOnly) {
      setIsDisabled(true);
    } else if (currentStatus === ContractStatus.Active || currentStatus === ContractStatus.Complete) {
      // Active or completed contracts cannot be changed
      setIsDisabled(true);
    } else {
      setIsDisabled(false);
    }
  }, [currentStatus, disabled, isStatusReadOnly]);

  const handleStatusChange = (newStatus: string) => {
    // Per requirements:
    // 1. New -> Pending: Admin, SuperAdmin, Manager can change
    // 2. Pending -> New: Only Admin, SuperAdmin can change back
    // 3. Active: Disable selector completely (handled in useEffect)

    if (currentStatus === ContractStatus.New && newStatus === ContractStatus.Pending) {
      // Admin, SuperAdmin, Manager can change from new to pending
      if (isAdmin || isManager) {
        setStatus(newStatus);
        onChange(newStatus);
      } else {
        toast.error("You don't have permission to change status to pending");
      }
    } else if (currentStatus === ContractStatus.Pending && newStatus === ContractStatus.New) {
      // Only admin or super_admin can change from pending to new
      if (isAdmin) {
        setStatus(newStatus);
        onChange(newStatus);
      } else {
        toast.error("Only administrators can change status back to new");
      }
    } else {
      // For other status changes
      setStatus(newStatus);
      onChange(newStatus);
    }
  };

  // If status is active, complete, or archived, show text instead of select
  if (isStatusReadOnly) {
    return (
      <div className="px-3 py-2 text-sm font-medium border rounded-md bg-muted">
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </div>
    );
  }

  return (
    <div className="w-full">
      <Select
        value={status}
        onValueChange={handleStatusChange}
        disabled={isDisabled}
      >
        <SelectTrigger className={`w-full ${isDisabled ? 'opacity-60' : ''}`}>
          <SelectValue placeholder="Select status" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ContractStatus.New}>
            New
          </SelectItem>
          <SelectItem value={ContractStatus.Pending}>
            Pending
          </SelectItem>
          {/* Removed Active and Complete options as per user's feedback */}
        </SelectContent>
      </Select>
    </div>
  );
}